import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const StarRow = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <button
        key={s}
        type="button"
        onClick={onChange ? () => onChange(s) : undefined}
        className={onChange ? "cursor-pointer" : "cursor-default"}
        data-testid={onChange ? `rating-star-${s}` : undefined}
        aria-label={`${s} stars`}
      >
        <Star size={onChange ? 24 : 14} fill={s <= value ? "#29FF87" : "transparent"} stroke="#29FF87" strokeWidth={1.5} />
      </button>
    ))}
  </div>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Please add your name and a review.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thanks! Your review will appear once approved.");
      setForm({ name: "", rating: 5, text: "" });
      setOpenForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="py-20 md:py-28 px-6 md:px-12 border-t border-[#29FF87]/15" data-testid="reviews-section">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="font-grotesk font-bold uppercase tracking-[0.3em] text-[#29FF87] text-xs mb-3">Feedback</p>
            <h2 className="font-grotesk font-extrabold uppercase text-3xl md:text-5xl text-white tracking-tight">What athletes <span className="glow-green">say</span> <span className="text-[#7BC79A] text-2xl">· {reviews.length}</span></h2>
          </div>
          <button
            onClick={() => setOpenForm(true)}
            className="self-start bg-[#29FF87] text-[#050805] px-6 py-3 rounded-md font-body text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#5CFFA8] transition-colors duration-300 glow-box"
            data-testid="write-review-button"
          >
            Write a review
          </button>
        </div>

        {reviews.length === 0 ? (
          <p className="font-body text-[#7BC79A] text-sm">Be the first to leave a review.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <motion.div
                key={r.id || i}
                initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
                className="rounded-xl border border-[#29FF87]/20 bg-[#0B120B] p-8"
                data-testid={`review-${i}`}
              >
                <StarRow value={r.rating} />
                <p className="mt-4 font-body text-sm text-[#C9F5DA] leading-relaxed">"{r.text}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-[#29FF87]/15 text-[#29FF87] flex items-center justify-center font-grotesk font-bold">{(r.name || "?")[0].toUpperCase()}</span>
                  <div>
                    <p className="font-body text-sm font-semibold text-white">{r.name}</p>
                    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-[#7BC79A]">Verified buyer</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {openForm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="review-modal">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#0B120B] border border-[#29FF87]/25 rounded-xl w-full max-w-lg p-8">
            <div className="flex justify-between items-center mb-8">
              <h3 className="font-grotesk font-bold text-xl uppercase tracking-[0.15em] text-white">Write a review</h3>
              <button onClick={() => setOpenForm(false)} className="text-zinc-400 hover:text-white" data-testid="close-review-modal"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-7">
              <div>
                <label className="font-body text-[10px] uppercase tracking-[0.25em] text-[#7BC79A]">Your rating</label>
                <div className="mt-3"><StarRow value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} /></div>
              </div>
              <div>
                <label className="font-body text-[10px] uppercase tracking-[0.25em] text-[#7BC79A]">Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-[#29FF87]/25 focus:border-[#29FF87] outline-none py-2 font-body text-sm text-white transition-colors" data-testid="review-name" />
              </div>
              <div>
                <label className="font-body text-[10px] uppercase tracking-[0.25em] text-[#7BC79A]">Review *</label>
                <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4}
                  className="w-full bg-transparent border border-[#29FF87]/25 focus:border-[#29FF87] outline-none p-3 font-body text-sm text-white resize-none mt-2 transition-colors" data-testid="review-text" />
              </div>
              <button type="submit" disabled={submitting}
                className="w-full bg-[#29FF87] text-[#050805] py-4 rounded-md font-body text-sm uppercase tracking-[0.25em] font-bold hover:bg-[#5CFFA8] disabled:opacity-50 transition-colors" data-testid="submit-review">
                {submitting ? "Submitting..." : "Submit review"}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </section>
  );
};
